import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * HeroProductLoop — auto-cycling product showcase à la Linear / Stripe / Notion.
 *
 * Lives inside the hero as the SINGLE showcase on the landing page.  Rotates
 * through the latest Viyora UI.  Screenshots live in /public/showcase/*.jpg
 * and are regenerated by `python /app/scripts/capture_showcase.py` — never
 * hardcoded old imagery.
 *
 * Behaviour contract (matches Linear / Framer / Stripe / Notion / Raycast):
 *   • Auto-play every 5.5s, infinite loop, no user interaction required.
 *   • Cross-fade + subtle scale (1.02 → 1.00) over 600ms.
 *   • Pause on hover.  Pause on drag / swipe.  Resume automatically.
 *   • Pause when tab is hidden.
 *   • Manual controls (arrows, dots, keyboard ←/→, mobile swipe) reset autoplay.
 *   • Respects prefers-reduced-motion (holds on slide 1).
 */

const SLIDES = [
  { slug: "01-dashboard",          tab: "AI Dashboard",           caption: "Live KPIs, AI alerts and today's schedule."              },
  { slug: "02-patient-360",        tab: "Patient 360°",           caption: "Timeline, X-rays, ledger, prescriptions — one screen."    },
  { slug: "03-calendar",           tab: "Smart Calendar",         caption: "Drag-and-drop chairs · Google Calendar sync."             },
  { slug: "04-dental-chart",       tab: "Dental Chart",           caption: "Interactive tooth-by-tooth charting with AI."             },
  { slug: "05-billing",            tab: "Billing & Payments",     caption: "GST invoices, wallet, outstanding, receipts."             },
  { slug: "06-lab-cases",          tab: "Lab Case Management",    caption: "Track cases, delivery dates and lab timeline."            },
  { slug: "07-inventory",          tab: "Inventory & Stock",      caption: "Batch/FIFO ledger with low-stock AI alerts."              },
  { slug: "08-whatsapp",           tab: "WhatsApp Automation",    caption: "Reminders, invoices and AI follow-ups."                   },
  { slug: "09-ai-copilot",         tab: "Viyora Copilot",         caption: "Your always-on Claude Sonnet 4.5 assistant."              },
  { slug: "10-analytics",          tab: "Doctor Analytics",       caption: "Revenue, treatments and retention per doctor."            },
  { slug: "11-data-migration",     tab: "Data Migration",         caption: "Import old records or add them manually — safely."        },
  { slug: "12-online-booking",     tab: "Online Booking",         caption: "Self-scheduling that respects every clinic rule."         },
];

const AUTOPLAY_MS   = 5500;
const TRANSITION_MS = 600;
const SWIPE_PX      = 40;

function prefersReducedMotion() {
  try {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export default function HeroProductLoop() {
  const [idx, setIdx]       = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef   = useRef(null);
  const startRef = useRef(Date.now());
  const rootRef  = useRef(null);
  const touchStartX = useRef(null);
  const reduced  = useRef(prefersReducedMotion());

  const total   = SLIDES.length;
  const current = SLIDES[idx];

  // Resets autoplay whenever we jump — used by arrows, dots, keyboard, swipe.
  const goto = useCallback((next) => {
    setIdx((next + total) % total);
    setProgress(0);
    startRef.current = Date.now();
  }, [total]);

  // Preload every image on mount so cross-fades are instant.
  useEffect(() => {
    SLIDES.forEach((s) => { const img = new Image(); img.src = `/showcase/${s.slug}.jpg`; });
  }, []);

  // Auto-play engine (RAF for a smooth progress bar; infinite loop via modulo)
  useEffect(() => {
    if (paused || reduced.current) return undefined;
    startRef.current = Date.now();
    const tick = () => {
      const p = Math.min(1, (Date.now() - startRef.current) / AUTOPLAY_MS);
      setProgress(p);
      if (p >= 1) {
        goto(idx + 1); // loops via (n + total) % total
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [idx, paused, goto]);

  // Pause when tab hidden
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Global keyboard nav (← / →) — works whenever landing page is focused.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); goto(idx + 1); }
      if (e.key === "ArrowLeft")  { e.preventDefault(); goto(idx - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, goto]);

  // Mobile swipe support
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > SWIPE_PX) goto(dx < 0 ? idx + 1 : idx - 1);
    touchStartX.current = null;
    setPaused(false);
  };

  return (
    <div
      ref={rootRef}
      data-testid="hero-product-loop"
      className="relative select-none"
      role="region"
      aria-roledescription="carousel"
      aria-label="Viyora product showcase — auto-cycling"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Browser window mockup */}
      <div className="relative rounded-2xl overflow-hidden bg-white border border-[#E5E7EB] shadow-[0_30px_80px_-30px_rgba(17,24,39,0.28)]">
        {/* Chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#EEF0F2] bg-[#FAFAFA]">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          </div>
          <div className="flex-1 mx-3">
            <div className="mx-auto max-w-sm rounded-md bg-white border border-[#E5E7EB] px-3 py-1 text-[11px] text-ink-soft font-medium truncate text-center">
              app.viyora.co.in <span className="text-ink-mid">/ {current.tab.toLowerCase().replace(/\s+/g, "-")}</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-pine">
            <span className="w-1.5 h-1.5 rounded-full bg-pine animate-pulse" /> Live
          </div>
        </div>

        {/* Slide stage — cross-fade + subtle scale (1.02 → 1.00) */}
        <div className="relative aspect-[16/10] bg-linen overflow-hidden" aria-live="polite">
          <AnimatePresence mode="sync">
            <motion.img
              key={current.slug}
              src={`/showcase/${current.slug}.jpg`}
              alt={`${current.tab} — Viyora`}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1.0 }}
              exit={{    opacity: 0, scale: 1.0 }}
              transition={{ duration: TRANSITION_MS / 1000, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover object-top will-change-[transform,opacity]"
              draggable={false}
              data-testid={`hero-slide-${current.slug}`}
            />
          </AnimatePresence>

          {/* Soft glass reflection sweep */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/15 via-transparent to-transparent" />

          {/* Feature label pill — cross-fades with the slide */}
          <AnimatePresence mode="sync">
            <motion.div
              key={`${current.slug}-label`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{    opacity: 0, y: -6 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="absolute left-4 bottom-4 max-w-[78%] rounded-xl bg-white/95 backdrop-blur-sm border border-[#EEF0F2] shadow-lg px-3.5 py-2.5"
              data-testid="hero-slide-label"
            >
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-pine">{current.tab}</div>
              <div className="text-xs text-ink font-semibold mt-0.5 leading-snug">{current.caption}</div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Prev / Next arrows — floated on the frame edges */}
      <button
        onClick={() => goto(idx - 1)}
        aria-label="Previous slide"
        data-testid="hero-loop-prev"
        className="absolute top-1/2 -translate-y-1/2 -left-3 sm:-left-4 w-9 h-9 rounded-full bg-white text-ink shadow-md border border-[#E5E7EB] grid place-items-center hover:bg-ink hover:text-white transition-colors z-10"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        onClick={() => goto(idx + 1)}
        aria-label="Next slide"
        data-testid="hero-loop-next"
        className="absolute top-1/2 -translate-y-1/2 -right-3 sm:-right-4 w-9 h-9 rounded-full bg-white text-ink shadow-md border border-[#E5E7EB] grid place-items-center hover:bg-ink hover:text-white transition-colors z-10"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Segment progress bar — one per slide, active fills with autoplay */}
      <div className="mt-4 flex items-center gap-1.5" data-testid="hero-loop-progress">
        {SLIDES.map((s, i) => {
          const active = i === idx;
          const past   = i <  idx;
          return (
            <button
              key={s.slug}
              onClick={() => goto(i)}
              aria-label={`Go to slide ${i + 1}: ${s.tab}`}
              aria-current={active ? "true" : undefined}
              data-testid={`hero-loop-dot-${i}`}
              className="group relative flex-1 h-1 rounded-full bg-[#E5E7EB] overflow-hidden hover:bg-[#D1D5DB] transition-colors"
            >
              <span
                className="absolute inset-y-0 left-0 bg-pine"
                style={{ width: active ? `${progress * 100}%` : past ? "100%" : "0%" }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
